import { useEffect, useRef, useCallback } from 'react';

/**
 * Selector for all focusable elements
 */
const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

export interface UseFocusTrapOptions {
  /**
   * Whether the focus trap is active
   */
  isActive: boolean;
  /**
   * Element to return focus to when trap is deactivated
   */
  returnFocusRef?: React.RefObject<HTMLElement | null> | undefined;
  /**
   * Whether to auto-focus the first focusable element on activation
   * @default true
   */
  autoFocus?: boolean;
  /**
   * Custom selector for the initial focus target (uses first focusable by default)
   */
  initialFocusSelector?: string;
}

export interface UseFocusTrapReturn {
  /**
   * Ref to attach to the container element that should trap focus
   */
  containerRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Get all focusable elements within the container
   */
  getFocusableElements: () => HTMLElement[];
  /**
   * Focus the first focusable element
   */
  focusFirst: () => void;
  /**
   * Focus the last focusable element
   */
  focusLast: () => void;
}

/**
 * Hook for trapping focus within a container element.
 *
 * Features:
 * - Traps Tab/Shift+Tab navigation within the container
 * - Auto-focuses first focusable element on activation
 * - Returns focus to trigger element on deactivation
 * - Handles dynamic content (recalculates focusable elements each time)
 *
 * @example
 * ```tsx
 * const { containerRef } = useFocusTrap({
 *   isActive: isOpen,
 *   returnFocusRef: buttonRef,
 * });
 *
 * return <div ref={containerRef}>...</div>;
 * ```
 */
export function useFocusTrap({
  isActive,
  returnFocusRef,
  autoFocus = true,
  initialFocusSelector,
}: UseFocusTrapOptions): UseFocusTrapReturn {
  const containerRef = useRef<HTMLDivElement>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  /**
   * Get all focusable elements within the container
   */
  const getFocusableElements = useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];
    const elements =
      containerRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    return Array.from(elements).filter((el) => {
      // Check if element is disabled
      if (el.hasAttribute('disabled')) return false;
      // Check visibility (getComputedStyle is more reliable than offsetParent in jsdom)
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden')
        return false;
      return true;
    });
  }, []);

  /**
   * Focus the first focusable element
   */
  const focusFirst = useCallback(() => {
    const elements = getFocusableElements();
    elements.at(0)?.focus();
  }, [getFocusableElements]);

  /**
   * Focus the last focusable element
   */
  const focusLast = useCallback(() => {
    const elements = getFocusableElements();
    elements.at(-1)?.focus();
  }, [getFocusableElements]);

  // Store previous active element and focus initial element on activation
  useEffect(() => {
    if (isActive) {
      // Store the currently focused element to restore later
      previousActiveElement.current = document.activeElement as HTMLElement;

      // Auto-focus on activation (with small delay to ensure render is complete)
      if (autoFocus) {
        requestAnimationFrame(() => {
          if (!containerRef.current) return;

          // Try custom initial focus selector first
          if (initialFocusSelector) {
            const initialElement =
              containerRef.current.querySelector<HTMLElement>(
                initialFocusSelector
              );
            if (initialElement) {
              initialElement.focus();
              return;
            }
          }

          // Fall back to first focusable element
          focusFirst();
        });
      }
    } else if (previousActiveElement.current) {
      // Deactivated: return focus to previous element or returnFocusRef
      const targetElement =
        returnFocusRef?.current ?? previousActiveElement.current;
      targetElement?.focus();
      previousActiveElement.current = null;
    }
  }, [isActive, autoFocus, initialFocusSelector, focusFirst, returnFocusRef]);

  // Handle tab key to trap focus
  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab') return;
      if (!containerRef.current) return;

      const focusableElements = getFocusableElements();
      const firstElement = focusableElements.at(0);
      const lastElement = focusableElements.at(-1);

      if (!firstElement || !lastElement) return;

      const activeElement = document.activeElement;

      // Shift+Tab on first element -> go to last
      if (event.shiftKey && activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
        return;
      }

      // Tab on last element -> go to first
      if (!event.shiftKey && activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
        return;
      }

      // If focus is outside container, bring it back
      if (!containerRef.current.contains(activeElement)) {
        event.preventDefault();
        if (event.shiftKey) {
          lastElement.focus();
        } else {
          firstElement.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isActive, getFocusableElements]);

  return {
    containerRef,
    getFocusableElements,
    focusFirst,
    focusLast,
  };
}

export default useFocusTrap;
